// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  admin/src/pages/VendorApplications.jsx
//  Copy to: C:\quickry\admin\src\pages\VendorApplications.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import {
  Search, Check, X, Clock, Eye, RefreshCw, ChevronRight,
  UtensilsCrossed, ShoppingBasket, Package, Pill,
  Building2, Phone, Mail, MapPin, FileText, CreditCard,
  AlertCircle, CheckCircle2, XCircle, Loader2, ArrowLeft,
  BadgeCheck, Ban, MoreHorizontal, Filter, Download,
  Star, Calendar, Hash,
} from "lucide-react";

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", greenLight:"#dcfce7",
  red:"#ef4444", redLight:"#fef2f2",
  yellow:"#f59e0b", yellowLight:"#fefce8",
  blue:"#2563eb", blueLight:"#eff6ff",
  purple:"#7c3aed", purpleLight:"#f5f3ff",
};

const BASE = typeof window !== "undefined" && window.location.hostname !== "localhost" ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";
const getToken = () => localStorage.getItem("adminToken") || localStorage.getItem("token") || "";
const req = async (method, url, body=null) => {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method,
      headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` },
      ...(body ? { body:JSON.stringify(body) } : {}),
    });
    return await res.json();
  } catch(e) { return { success:false, message:e.message }; }
};

// ── Config maps ───────────────────────────────────
const TYPE_MAP = {
  restaurant: { label:"Restaurant",  icon:UtensilsCrossed, color:T.primary,  bg:T.primaryLight },
  grocery:    { label:"Grocery",     icon:ShoppingBasket,  color:T.green,    bg:T.greenLight   },
  courier:    { label:"Courier",     icon:Package,         color:T.blue,     bg:T.blueLight    },
  medicine:   { label:"Medicine",    icon:Pill,            color:T.purple,   bg:T.purpleLight  },
};

const STATUS_MAP = {
  pending:      { label:"Pending",      color:T.yellow, bg:T.yellowLight, icon:Clock         },
  under_review: { label:"Under Review", color:T.blue,   bg:T.blueLight,   icon:Eye           },
  approved:     { label:"Approved",     color:T.green,  bg:T.greenLight,  icon:CheckCircle2  },
  rejected:     { label:"Rejected",     color:T.red,    bg:T.redLight,    icon:XCircle       },
  suspended:    { label:"Suspended",    color:T.textSub,bg:T.bg,          icon:Ban           },
};

// ── Status Badge ──────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  const Icon = s.icon;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:s.bg, color:s.color, fontSize:"0.68rem", fontWeight:700, padding:"3px 9px", borderRadius:100 }}>
      <Icon size={10}/> {s.label}
    </span>
  );
}

// ── Reject Modal ──────────────────────────────────
function RejectModal({ vendor, onClose, onReject }) {
  const [reason,   setReason]   = useState("");
  const [loading,  setLoading]  = useState(false);

  const REASONS = [
    "Documents incomplete ya invalid",
    "GST number verify nahi hua",
    "FSSAI license missing (food business)",
    "Bank details incorrect",
    "Business address unverifiable",
    "Duplicate application",
    "Service area coverage nahi",
  ];

  const handle = async () => {
    if (!reason.trim()) return alert("Rejection reason required!");
    setLoading(true);
    const res = await req("PATCH", `/vendors/${vendor._id}/reject`, { status:"rejected", rejectionReason: reason });
    setLoading(false);
    if (res.success) onReject(res.data);
    else alert(res.message);
  };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.white,borderRadius:20,width:"100%",maxWidth:440,boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ padding:"18px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1rem",color:T.red }}>Reject Application</div>
          <button onClick={onClose} style={{ background:T.bg,border:"none",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
            <X size={15} color={T.textSub}/>
          </button>
        </div>
        <div style={{ padding:20 }}>
          <div style={{ fontSize:"0.82rem",color:T.textSub,marginBottom:12 }}>
            Rejecting: <strong style={{ color:T.text }}>{vendor.businessName}</strong>
          </div>

          {/* Quick reason chips */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.5px" }}>Quick Select</div>
            <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
              {REASONS.map(r => (
                <button key={r} onClick={()=>setReason(r)}
                  style={{ background:reason===r?T.redLight:T.bg,border:`1px solid ${reason===r?T.red:T.border}`,borderRadius:8,padding:"5px 10px",fontSize:"0.72rem",fontWeight:600,color:reason===r?T.red:T.textSub,cursor:"pointer",fontFamily:"inherit",transition:"all .15s" }}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Custom reason */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px" }}>Custom Reason</div>
            <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
              placeholder="Type rejection reason..."
              style={{ width:"100%",border:`1.5px solid ${T.border}`,borderRadius:10,padding:"10px 12px",fontSize:"0.85rem",fontFamily:"inherit",color:T.text,resize:"none",outline:"none",boxSizing:"border-box" }}
              onFocus={e=>e.target.style.borderColor=T.red}
              onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>

          <div style={{ display:"flex",gap:10 }}>
            <button onClick={onClose}
              style={{ flex:1,background:T.bg,border:`1.5px solid ${T.border}`,borderRadius:12,padding:"11px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:T.textSub }}>
              Cancel
            </button>
            <button onClick={handle} disabled={loading||!reason.trim()}
              style={{ flex:2,background:T.red,border:"none",borderRadius:12,padding:"11px",fontWeight:800,cursor:loading||!reason.trim()?"not-allowed":"pointer",color:"#fff",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8,opacity:loading||!reason.trim()?0.7:1 }}>
              {loading?<><Loader2 size={14} style={{ animation:"spin .8s linear infinite" }}/> Rejecting...</>:<><XCircle size={14}/> Reject Application</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Vendor Detail Panel ───────────────────────────
function VendorDetail({ vendor, onClose, onApprove, onReject, onStatusChange }) {
  const [approving, setApproving] = useState(false);
  const [showReject,setShowReject]= useState(false);
  const [notes,     setNotes]     = useState(vendor.notes || "");
  const [savingNote,setSavingNote]= useState(false);

  const typeInfo   = TYPE_MAP[vendor.businessType]   || TYPE_MAP.restaurant;
  const TypeIcon   = typeInfo.icon;

  const handleApprove = async () => {
    setApproving(true);
    const res = await req("PATCH", `/vendors/${vendor._id}/approve`);
    setApproving(false);
    if (res.success) onApprove(res.data);
    else alert(res.message);
  };

  const handleStatusChange = async (status) => {
    const res = await req("PUT", `/vendors/${vendor._id}/status`, { status });
    if (res.success) onStatusChange(res.data);
  };

  const saveNote = async () => {
    setSavingNote(true);
    await req("PUT", `/vendors/${vendor._id}/status`, { status:vendor.status, adminNote: notes });
    setSavingNote(false);
  };

  const InfoRow = ({ icon:Icon, label, value, mono=false }) => (
    <div style={{ display:"flex",gap:10,alignItems:"flex-start",marginBottom:12 }}>
      <div style={{ width:32,height:32,borderRadius:9,background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
        <Icon size={14} color={T.textSub}/>
      </div>
      <div>
        <div style={{ fontSize:"0.65rem",color:T.textMuted,fontWeight:600,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:2 }}>{label}</div>
        <div style={{ fontSize:"0.84rem",color:T.text,fontWeight:600,fontFamily:mono?"monospace":"inherit",wordBreak:"break-all" }}>{value||"—"}</div>
      </div>
    </div>
  );

  const DocRow = ({ label, number, doc }) => (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px",borderBottom:`1px solid ${T.border}`,background:T.white }}>
      <div>
        <div style={{ fontWeight:600,fontSize:"0.82rem",color:T.text }}>{label}</div>
        {number && <div style={{ fontSize:"0.72rem",color:T.textMuted,marginTop:2,fontFamily:"monospace" }}>{number}</div>}
      </div>
      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
        {doc
          ? <span style={{ background:T.greenLight,color:T.green,fontSize:"0.68rem",fontWeight:700,padding:"3px 8px",borderRadius:100,display:"flex",alignItems:"center",gap:3 }}><Check size={10}/> Uploaded</span>
          : <span style={{ background:T.redLight,color:T.red,fontSize:"0.68rem",fontWeight:700,padding:"3px 8px",borderRadius:100,display:"flex",alignItems:"center",gap:3 }}><X size={10}/> Missing</span>
        }
      </div>
    </div>
  );

  return (
    <>
      <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:400,display:"flex",alignItems:"flex-start",justifyContent:"flex-end" }} onClick={onClose}>
        <div onClick={e=>e.stopPropagation()} style={{ width:"100%",maxWidth:520,height:"100vh",background:T.bg,overflowY:"auto",boxShadow:"-8px 0 40px rgba(0,0,0,0.15)",display:"flex",flexDirection:"column" }}>

          {/* Header */}
          <div style={{ background:T.white,padding:"16px 20px",borderBottom:`1px solid ${T.border}`,position:"sticky",top:0,zIndex:10 }}>
            <div style={{ display:"flex",alignItems:"center",gap:12 }}>
              <button onClick={onClose} style={{ background:T.bg,border:"none",borderRadius:"50%",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
                <ArrowLeft size={16} color={T.textSub}/>
              </button>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1rem",color:T.text }}>{vendor.businessName}</div>
                <div style={{ fontSize:"0.7rem",color:T.textMuted,marginTop:1 }}>{vendor.ownerName} · {vendor.email}</div>
              </div>
              <StatusBadge status={vendor.status}/>
            </div>
          </div>

          <div style={{ padding:16,flex:1 }}>

            {/* Business type + applied date */}
            <div style={{ background:T.white,borderRadius:14,padding:"14px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:12,border:`1px solid ${T.border}` }}>
              <div style={{ width:48,height:48,borderRadius:14,background:typeInfo.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                <TypeIcon size={22} color={typeInfo.color}/>
              </div>
              <div>
                <div style={{ fontWeight:700,fontSize:"0.9rem",color:T.text }}>{typeInfo.label}</div>
                <div style={{ fontSize:"0.72rem",color:T.textMuted,display:"flex",alignItems:"center",gap:4,marginTop:2 }}>
                  <Calendar size={11}/> Applied {new Date(vendor.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                </div>
              </div>
              {vendor.status === "rejected" && vendor.rejectionReason && (
                <div style={{ marginLeft:"auto",background:T.redLight,borderRadius:10,padding:"8px 12px",maxWidth:180 }}>
                  <div style={{ fontSize:"0.65rem",fontWeight:700,color:T.red,marginBottom:2 }}>REJECTION REASON</div>
                  <div style={{ fontSize:"0.72rem",color:T.red }}>{vendor.rejectionReason}</div>
                </div>
              )}
            </div>

            {/* Business Details */}
            <div style={{ background:T.white,borderRadius:14,padding:"16px",marginBottom:12,border:`1px solid ${T.border}` }}>
              <div style={{ fontWeight:700,fontSize:"0.8rem",color:T.textMuted,letterSpacing:"1px",textTransform:"uppercase",marginBottom:14 }}>Business Details</div>
              <InfoRow icon={Building2} label="Business Name"  value={vendor.businessName}/>
              <InfoRow icon={Hash}      label="Owner Name"     value={vendor.ownerName}/>
              <InfoRow icon={Phone}     label="Phone"          value={vendor.phone}/>
              <InfoRow icon={Mail}      label="Email"          value={vendor.email}/>
              <InfoRow icon={MapPin}    label="Area"           value={[vendor.area, vendor.city, vendor.pincode].filter(Boolean).join(", ")}/>
              {vendor.description && <InfoRow icon={FileText} label="Description" value={vendor.description}/>}
            </div>

            {/* Documents */}
            <div style={{ background:T.white,borderRadius:14,overflow:"hidden",marginBottom:12,border:`1px solid ${T.border}` }}>
              <div style={{ padding:"14px 16px",borderBottom:`1px solid ${T.border}` }}>
                <div style={{ fontWeight:700,fontSize:"0.8rem",color:T.textMuted,letterSpacing:"1px",textTransform:"uppercase" }}>Documents</div>
              </div>
              <DocRow label="GST Certificate"   number={vendor.documents?.gstNumber}    doc={vendor.documents?.gstDoc}/>
              {["restaurant","grocery","medicine"].includes(vendor.businessType) && (
                <DocRow label="FSSAI License"   number={vendor.documents?.fssaiNumber}  doc={vendor.documents?.fssaiDoc}/>
              )}
              <DocRow label="PAN Card"          number={vendor.documents?.panNumber}    doc={vendor.documents?.panDoc}/>
              <DocRow label="Aadhar Card"       number={vendor.documents?.aadharNumber} doc={vendor.documents?.aadharDoc}/>
              <DocRow label="Shop Act License"  number={null}                           doc={vendor.documents?.shopActDoc}/>
            </div>

            {/* Bank Details */}
            <div style={{ background:T.white,borderRadius:14,padding:"16px",marginBottom:12,border:`1px solid ${T.border}` }}>
              <div style={{ fontWeight:700,fontSize:"0.8rem",color:T.textMuted,letterSpacing:"1px",textTransform:"uppercase",marginBottom:14 }}>Bank Details</div>
              <InfoRow icon={CreditCard} label="Account Holder" value={vendor.bankDetails?.accountHolder}/>
              <InfoRow icon={Hash}       label="Account Number" value={vendor.bankDetails?.accountNumber} mono/>
              <InfoRow icon={Hash}       label="IFSC Code"      value={vendor.bankDetails?.ifscCode}      mono/>
              <InfoRow icon={Building2}  label="Bank Name"      value={vendor.bankDetails?.bankName}/>
              {vendor.bankDetails?.upiId && <InfoRow icon={Phone} label="UPI ID" value={vendor.bankDetails?.upiId}/>}
            </div>

            {/* Admin Notes */}
            <div style={{ background:T.white,borderRadius:14,padding:"16px",marginBottom:12,border:`1px solid ${T.border}` }}>
              <div style={{ fontWeight:700,fontSize:"0.8rem",color:T.textMuted,letterSpacing:"1px",textTransform:"uppercase",marginBottom:10 }}>Internal Notes</div>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
                placeholder="Add internal notes about this vendor..."
                style={{ width:"100%",border:`1.5px solid ${T.border}`,borderRadius:10,padding:"10px 12px",fontSize:"0.83rem",fontFamily:"inherit",color:T.text,resize:"none",outline:"none",boxSizing:"border-box",marginBottom:8 }}/>
              <button onClick={saveNote} disabled={savingNote}
                style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"7px 14px",fontSize:"0.78rem",fontWeight:600,cursor:"pointer",color:T.textSub,fontFamily:"inherit" }}>
                {savingNote?"Saving...":"Save Note"}
              </button>
            </div>

            {/* Change status */}
            {vendor.status !== "approved" && vendor.status !== "rejected" && (
              <div style={{ background:T.white,borderRadius:14,padding:"14px 16px",marginBottom:12,border:`1px solid ${T.border}` }}>
                <div style={{ fontWeight:700,fontSize:"0.8rem",color:T.textMuted,letterSpacing:"1px",textTransform:"uppercase",marginBottom:10 }}>Change Status</div>
                <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                  {["pending","under_review"].filter(s=>s!==vendor.status).map(s => {
                    const sm = STATUS_MAP[s];
                    return (
                      <button key={s} onClick={()=>handleStatusChange(s)}
                        style={{ background:sm.bg,border:`1px solid ${sm.color}20`,borderRadius:9,padding:"7px 14px",fontSize:"0.78rem",fontWeight:700,cursor:"pointer",color:sm.color,fontFamily:"inherit" }}>
                        Mark as {sm.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {(vendor.status === "pending" || vendor.status === "under_review") && (
            <div style={{ padding:"14px 16px",background:T.white,borderTop:`1px solid ${T.border}`,display:"flex",gap:10,position:"sticky",bottom:0 }}>
              <button onClick={()=>setShowReject(true)}
                style={{ flex:1,background:T.redLight,border:`1.5px solid ${T.red}20`,borderRadius:12,padding:"12px",fontWeight:800,cursor:"pointer",color:T.red,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
                <X size={15}/> Reject
              </button>
              <button onClick={handleApprove} disabled={approving}
                style={{ flex:2,background:`linear-gradient(135deg,#15803d,${T.green})`,border:"none",borderRadius:12,padding:"12px",fontWeight:800,cursor:approving?"not-allowed":"pointer",color:"#fff",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6,boxShadow:"0 4px 14px rgba(22,163,74,0.3)" }}>
                {approving?<><Loader2 size={15} style={{ animation:"spin .8s linear infinite" }}/> Approving...</>:<><CheckCircle2 size={15}/> Approve Vendor</>}
              </button>
            </div>
          )}

          {vendor.status === "approved" && (
            <div style={{ padding:"14px 16px",background:T.greenLight,borderTop:`1px solid #bbf7d0`,display:"flex",alignItems:"center",gap:10 }}>
              <BadgeCheck size={18} color={T.green}/>
              <span style={{ fontWeight:700,color:T.green,fontSize:"0.85rem" }}>
                Approved on {new Date(vendor.approvedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
              </span>
              <button onClick={()=>handleStatusChange("suspended")} style={{ marginLeft:"auto",background:"none",border:`1px solid ${T.textMuted}30`,borderRadius:8,padding:"5px 10px",fontSize:"0.72rem",fontWeight:600,cursor:"pointer",color:T.textSub,fontFamily:"inherit" }}>
                Suspend
              </button>
            </div>
          )}
        </div>
      </div>

      {showReject && (
        <RejectModal
          vendor={vendor}
          onClose={()=>setShowReject(false)}
          onReject={(data)=>{ onReject(data); setShowReject(false); }}
        />
      )}
    </>
  );
}

// ── MAIN PAGE ─────────────────────────────────────
export default function VendorApplications() {
  const [vendors,  setVendors]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [stats,    setStats]    = useState({});
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType,   setFilterType]   = useState("");
  const [toast,    setToast]    = useState("");

  const showToast = m => { setToast(m); setTimeout(()=>setToast(""),2500); };

  const load = async () => {
    setLoading(true);
    try {
      const allRes = await req("GET", "/vendors");

      if (!allRes.success) {
        showToast("Error: " + (allRes.message || "Backend se data nahi aaya"));
        setLoading(false);
        return;
      }

      const all = allRes.data || [];

      setStats({
        total:        all.length,
        pending:      all.filter(v => v.status === "pending").length,
        under_review: all.filter(v => v.status === "under_review").length,
        approved:     all.filter(v => v.status === "approved").length,
        rejected:     all.filter(v => v.status === "rejected").length,
      });

      // Frontend filtering
      let filtered = [...all];
      if (filterStatus) filtered = filtered.filter(v => v.status === filterStatus);
      if (filterType)   filtered = filtered.filter(v => (v.businessType||"").toLowerCase() === filterType.toLowerCase());
      if (search)       filtered = filtered.filter(v =>
        v.businessName?.toLowerCase().includes(search.toLowerCase()) ||
        v.ownerName?.toLowerCase().includes(search.toLowerCase()) ||
        v.email?.toLowerCase().includes(search.toLowerCase()) ||
        v.phone?.includes(search)
      );

      setVendors(filtered);
    } catch (e) {
      showToast("Network error: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterStatus, filterType]);

  useEffect(() => {
    const t = setTimeout(() => load(), 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleApprove = (updated) => {
    setVendors(v => v.map(x => x._id===updated._id ? updated : x));
    setSelected(updated);
    showToast(`${updated.businessName} approved! ✅`);
  };

  const handleReject = (updated) => {
    setVendors(v => v.map(x => x._id===updated._id ? updated : x));
    setSelected(null);
    showToast("Application rejected");
  };

  const handleStatusChange = (updated) => {
    setVendors(v => v.map(x => x._id===updated._id ? updated : x));
    setSelected(updated);
  };

  const STATUS_FILTERS = [
    { val:"",             label:"All",          count: stats.total         },
    { val:"pending",      label:"Pending",      count: stats.pending       },
    { val:"under_review", label:"Under Review", count: stats.under_review  },
    { val:"approved",     label:"Approved",     count: stats.approved      },
    { val:"rejected",     label:"Rejected",     count: stats.rejected      },
  ];

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed",top:80,left:"50%",transform:"translateX(-50%)",background:T.text,color:"#fff",padding:"10px 22px",borderRadius:100,fontSize:"0.82rem",fontWeight:600,zIndex:600,display:"flex",alignItems:"center",gap:8,boxShadow:"0 4px 20px rgba(0,0,0,0.2)" }}>
          <Check size={14} color={T.green}/> {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.1rem",color:T.text }}>Vendor Applications</div>
          <div style={{ fontSize:"0.72rem",color:T.textMuted,marginTop:2 }}>Review aur approve karo new vendor applications</div>
        </div>
        <button onClick={load}
          style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:"0.78rem",fontWeight:600,color:T.textSub,fontFamily:"inherit" }}>
          <RefreshCw size={14} style={loading?{animation:"spin 1s linear infinite"}:{}}/> Refresh
        </button>
      </div>

      {/* Stats cards */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:20 }}>
        {[
          { label:"Total",       val:stats.total,        color:T.primary, bg:T.primaryLight },
          { label:"Pending",     val:stats.pending,      color:T.yellow,  bg:T.yellowLight  },
          { label:"Under Review",val:stats.under_review, color:T.blue,    bg:T.blueLight    },
          { label:"Approved",    val:stats.approved,     color:T.green,   bg:T.greenLight   },
          { label:"Rejected",    val:stats.rejected,     color:T.red,     bg:T.redLight     },
        ].map((x,i) => (
          <div key={i} onClick={()=>setFilterStatus(x.label==="Total"?"":x.label.toLowerCase().replace(" ","_"))}
            style={{ background:x.bg,borderRadius:12,padding:"13px 14px",border:`1px solid ${T.border}`,cursor:"pointer",transition:"transform .15s",outline:filterStatus===(x.label==="Total"?"":x.label.toLowerCase().replace(" ","_"))?`2px solid ${x.color}`:"none" }}
            onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
            onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
            <div style={{ fontWeight:900,fontSize:"1.5rem",color:x.color,fontFamily:"'Fraunces',Georgia,serif" }}>{loading?"—":x.val||0}</div>
            <div style={{ fontSize:"0.7rem",color:T.textSub,fontWeight:600,marginTop:2 }}>{x.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center" }}>
        {/* Search */}
        <div style={{ display:"flex",alignItems:"center",gap:8,background:T.white,border:`1.5px solid ${T.border}`,borderRadius:10,padding:"8px 12px",flex:1,maxWidth:300 }}>
          <Search size={14} color={T.textMuted}/>
          <input placeholder="Search vendors..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ border:"none",background:"transparent",color:T.text,fontSize:"0.83rem",outline:"none",fontFamily:"inherit",width:"100%" }}/>
        </div>

        {/* Status filter tabs */}
        <div style={{ display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none" }}>
          {STATUS_FILTERS.map(f => (
            <button key={f.val} onClick={()=>setFilterStatus(f.val)}
              style={{ flexShrink:0,padding:"7px 14px",borderRadius:10,border:`1.5px solid ${filterStatus===f.val?T.primary:T.border}`,background:filterStatus===f.val?T.primaryLight:"#fff",cursor:"pointer",fontSize:"0.78rem",fontWeight:filterStatus===f.val?700:500,color:filterStatus===f.val?T.primary:T.textSub,fontFamily:"inherit",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5 }}>
              {f.label}
              {f.count !== undefined && <span style={{ background:filterStatus===f.val?T.primary:T.bg,color:filterStatus===f.val?"#fff":T.textMuted,fontSize:"0.6rem",fontWeight:700,padding:"1px 6px",borderRadius:100 }}>{f.count||0}</span>}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <select value={filterType} onChange={e=>setFilterType(e.target.value)}
          style={{ background:T.white,border:`1.5px solid ${T.border}`,borderRadius:10,padding:"8px 12px",fontSize:"0.83rem",fontFamily:"inherit",color:T.textSub,cursor:"pointer",outline:"none" }}>
          <option value="">All Types</option>
          <option value="restaurant">Restaurant</option>
          <option value="grocery">Grocery</option>
          <option value="courier">Courier</option>
          <option value="medicine">Medicine</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background:T.white,borderRadius:16,border:`1px solid ${T.border}`,overflow:"hidden" }}>
        {loading ? (
          <div style={{ padding:60,textAlign:"center",color:T.textMuted }}>
            <Loader2 size={28} style={{ animation:"spin 1s linear infinite",color:T.primary,marginBottom:10 }}/>
            <div>Loading applications...</div>
          </div>
        ) : vendors.length === 0 ? (
          <div style={{ padding:60,textAlign:"center",color:T.textMuted }}>
            <FileText size={44} color={T.border} style={{ marginBottom:12 }}/>
            <div style={{ fontWeight:600,marginBottom:4 }}>No applications found</div>
            <div style={{ fontSize:"0.78rem" }}>Try adjusting filters</div>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%",borderCollapse:"collapse",minWidth:640 }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${T.border}`,background:T.bg }}>
                  {["Business","Type","Contact","Applied","Status","Action"].map(h => (
                    <th key={h} style={{ padding:"11px 16px",textAlign:"left",fontSize:"0.62rem",fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"1px",whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendors.map((v,i) => {
                  const typeInfo = TYPE_MAP[v.businessType] || TYPE_MAP.restaurant;
                  const TypeIcon = typeInfo.icon;
                  return (
                    <tr key={v._id}
                      style={{ borderBottom:`1px solid ${T.border}`,animation:`fadeUp .3s ${i*.03}s ease both`,cursor:"pointer",transition:"background .15s" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#f8fafc"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                      onClick={()=>setSelected(v)}>
                      <td style={{ padding:"12px 16px" }}>
                        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                          <div style={{ width:36,height:36,borderRadius:10,background:typeInfo.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                            <TypeIcon size={16} color={typeInfo.color}/>
                          </div>
                          <div>
                            <div style={{ fontWeight:700,fontSize:"0.85rem",color:T.text }}>{v.businessName}</div>
                            <div style={{ fontSize:"0.7rem",color:T.textMuted }}>{v.ownerName}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <span style={{ background:typeInfo.bg,color:typeInfo.color,fontSize:"0.68rem",fontWeight:700,padding:"3px 9px",borderRadius:100 }}>{typeInfo.label}</span>
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <div style={{ fontSize:"0.78rem",color:T.textSub }}>{v.phone}</div>
                        <div style={{ fontSize:"0.7rem",color:T.textMuted }}>{v.email}</div>
                      </td>
                      <td style={{ padding:"12px 16px",fontSize:"0.75rem",color:T.textMuted,whiteSpace:"nowrap" }}>
                        {new Date(v.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <StatusBadge status={v.status}/>
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <button onClick={e=>{e.stopPropagation();setSelected(v);}}
                          style={{ background:T.primaryLight,border:"none",borderRadius:9,padding:"7px 14px",color:T.primary,fontWeight:700,fontSize:"0.75rem",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5 }}>
                          <Eye size={13}/> Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <VendorDetail
          vendor={selected}
          onClose={()=>setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}